/**
 * Canvas 2D 文字绘制工具，供 circle/rect/triangle/vector 的 export.ts 调用。
 * 支持横排多行文字与竖排（writing-mode: vertical-rl）逐字绘制。
 */
import type {
  CircleElement,
  RectangleElement,
  TriangleElement,
  VectorElement,
} from '@/components/canvas/document/publicTypes';
import { resolveShapeTextStyle, type ResolvedShapeTextStyle } from './shapeTextStyle';

type ShapeTextElement = RectangleElement | CircleElement | TriangleElement | VectorElement;

interface TextBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * 在 ctx 上指定矩形区域内绘制图形内嵌文字。
 * @param ctx    Canvas 2D 上下文（调用前需已完成 save，本函数内部不 save/restore）
 * @param element 带有内嵌文字 data 的图形元素
 * @param bounds 绘制区域（相对于整个画布的坐标）
 */
export function drawShapeText(
  ctx: CanvasRenderingContext2D,
  element: ShapeTextElement,
  bounds: TextBounds,
): void {
  const text = element.data.text?.content ?? '';
  if (!text) return;

  const style = resolveShapeTextStyle(element);
  const isVertical = style.writingMode === 'vertical-rl';

  ctx.save();
  ctx.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize}px "${style.fontFamily}"`;
  ctx.fillStyle = style.color;

  if (isVertical) {
    drawVerticalText(ctx, text, style, bounds);
  } else {
    drawHorizontalText(ctx, text, style, bounds);
  }

  ctx.restore();
}

// ── 横排多行文字 ──────────────────────────────────────────────────────────────

function drawHorizontalText(
  ctx: CanvasRenderingContext2D,
  text: string,
  style: ResolvedShapeTextStyle,
  bounds: TextBounds,
): void {
  const lines = text.split('\n');
  const lineHeight = style.fontSize * 1.25;
  const totalTextHeight = lines.length * lineHeight;

  // 计算起始 y（垂直对齐）
  let startY: number;
  if (style.verticalAlign === 'top') {
    startY = bounds.y + style.fontSize;
  } else if (style.verticalAlign === 'bottom') {
    startY = bounds.y + bounds.height - totalTextHeight + style.fontSize;
  } else {
    startY = bounds.y + (bounds.height - totalTextHeight) / 2 + style.fontSize;
  }

  // 水平对齐
  ctx.textAlign = style.textAlign;
  ctx.textBaseline = 'alphabetic';

  let anchorX: number;
  if (style.textAlign === 'left') anchorX = bounds.x;
  else if (style.textAlign === 'right') anchorX = bounds.x + bounds.width;
  else anchorX = bounds.x + bounds.width / 2;

  // 裁剪至 bounds 区域
  ctx.save();
  ctx.rect(bounds.x, bounds.y, bounds.width, bounds.height);
  ctx.clip();

  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], anchorX, startY + i * lineHeight);
  }

  ctx.restore();
}

// ── 竖排逐字绘制（vertical-rl：从右到左，自上而下） ──────────────────────────

function drawVerticalText(
  ctx: CanvasRenderingContext2D,
  text: string,
  style: ResolvedShapeTextStyle,
  bounds: TextBounds,
): void {
  // \n 分割为多列（每"行"输入对应一列竖排文字）
  const columns = text.split('\n');
  const charSize = style.fontSize;
  const colGap = charSize * 0.15;
  const colWidth = charSize + colGap;
  const totalColumnsWidth = columns.length * colWidth - colGap;

  // 水平方向（textAlign）控制列组的整体位置
  let startX: number;
  if (style.textAlign === 'left') {
    startX = bounds.x + colWidth / 2;
  } else if (style.textAlign === 'right') {
    // vertical-rl：最右侧列先绘制
    startX = bounds.x + bounds.width - colWidth / 2;
  } else {
    // 居中
    const offset = (bounds.width - totalColumnsWidth) / 2;
    startX = bounds.x + bounds.width - offset - colWidth / 2;
  }

  ctx.save();
  ctx.rect(bounds.x, bounds.y, bounds.width, bounds.height);
  ctx.clip();

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // vertical-rl：从右向左绘制每一列
  for (let ci = 0; ci < columns.length; ci++) {
    const colX = startX - ci * colWidth;
    const chars = columns[ci];
    const totalColHeight = chars.length * charSize;

    // 垂直对齐（verticalAlign）控制每列文字的起始 y
    let charStartY: number;
    if (style.verticalAlign === 'top') {
      charStartY = bounds.y + charSize / 2;
    } else if (style.verticalAlign === 'bottom') {
      charStartY = bounds.y + bounds.height - totalColHeight + charSize / 2;
    } else {
      charStartY = bounds.y + (bounds.height - totalColHeight) / 2 + charSize / 2;
    }

    for (let j = 0; j < chars.length; j++) {
      const charY = charStartY + j * charSize;
      ctx.fillText(chars[j], colX, charY);
    }
  }

  ctx.restore();
}
